import { useState, useEffect, useCallback } from 'react';
import { Inbox, Mail, Phone, Building2, User, RefreshCw, Check } from 'lucide-react';

interface PremiumInquiry {
  id: string;
  name: string;
  company_name: string;
  phone: string;
  email: string;
  message: string | null;
  is_read: boolean;
  created_at: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminPremiumInquiriesTab() {
  const [inquiries, setInquiries] = useState<PremiumInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/premium-inquiries', { credentials: 'include' });
      if (!res.ok) throw new Error('인증 신청 목록을 불러오는데 실패했습니다.');
      const data = await res.json();
      setInquiries(data.inquiries || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const setRead = async (id: string, isRead: boolean) => {
    // 낙관적 업데이트
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, is_read: isRead } : i)));
    try {
      await fetch(`/api/v1/admin/premium-inquiries/${id}/read`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_read: isRead }),
      });
    } catch {
      // 실패 시 롤백
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, is_read: !isRead } : i)));
    }
  };

  const unreadCount = inquiries.filter((i) => !i.is_read).length;

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <Inbox className="h-6 w-6 text-accent" />
          우수 인증 신청 현황
          {unreadCount > 0 && (
            <span className="rounded-full bg-red-500 px-2.5 py-0.5 text-sm font-semibold text-white">
              미확인 {unreadCount}
            </span>
          )}
        </h1>
        <button
          onClick={fetchInquiries}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          새로고침
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-700">{error}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400">불러오는 중...</div>
      ) : inquiries.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-gray-500">
          접수된 인증 신청이 없습니다.
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inq) => (
            <div
              key={inq.id}
              className={`rounded-xl border bg-white p-5 shadow-sm transition-colors ${
                inq.is_read ? 'border-gray-200' : 'border-accent/40 bg-accent-50/30'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="flex items-center gap-1.5 font-bold text-gray-900">
                      {!inq.is_read && <span className="h-2 w-2 rounded-full bg-red-500" />}
                      <Building2 className="h-4 w-4 text-gray-400" />
                      {inq.company_name}
                    </span>
                    <span className="flex items-center gap-1.5 text-sm text-gray-600">
                      <User className="h-4 w-4 text-gray-400" />
                      {inq.name}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                    <a href={`tel:${inq.phone}`} className="flex items-center gap-1.5 hover:text-primary">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {inq.phone}
                    </a>
                    <a href={`mailto:${inq.email}`} className="flex items-center gap-1.5 hover:text-primary">
                      <Mail className="h-4 w-4 text-gray-400" />
                      {inq.email}
                    </a>
                  </div>
                  {inq.message && (
                    <p className="mt-3 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                      {inq.message}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span className="text-xs text-gray-400">{formatDate(inq.created_at)}</span>
                  <button
                    onClick={() => setRead(inq.id, !inq.is_read)}
                    className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      inq.is_read
                        ? 'border border-gray-300 text-gray-500 hover:bg-gray-50'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    <Check className="h-3.5 w-3.5" />
                    {inq.is_read ? '안읽음으로' : '확인 처리'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
